import React, { memo, useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import type { ThreadSortBy } from '@/types/residents/corkBoard';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { AnchoredDropdownItem } from '@/components/common/admin/desktop/dropdown/AnchoredDropdownItem';
import { HighlightPlate } from '@/components/shared/redesign/HighlightPlate';
import { useSpacing } from '@/hooks/shared/useSpacing';

interface NewsfeedSortRowProps {
  postCount: number;
  sortBy: ThreadSortBy;
  onSortChange: (sortBy: ThreadSortBy) => void;
}

export const NewsfeedSortRow: React.FC<NewsfeedSortRowProps> = memo(({
  postCount,
  sortBy,
  onSortChange,
}) => {
  const { colors } = useThemeColors();
  const space = useSpacing();
  // On web the selected option is drawn on a plate the shape of the hover
  // plate; off web the row keeps its own fill.
  const isWeb = Platform.OS === 'web';
  const [isOpen, setIsOpen] = useState(false);
  const currentLabel = CORK_BOARD_SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label ?? 'Sort';

  const handleSelect = useCallback((value: ThreadSortBy) => {
    onSortChange(value);
    setIsOpen(false);
  }, [onSortChange]);

  return (
    <View style={styles.container}>
      <Text style={[styles.postCount, { color: colors.text.dimmer }]}>{postCount} posts</Text>

      <Hoverable
        hover="link"
        style={styles.sortTrigger}
        onPress={() => setIsOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={`Sort by ${currentLabel}`}
        accessibilityState={{ expanded: isOpen }}
      >
        {({ textStyle }) => (
          <>
            <Text style={[styles.sortLabel, textStyle]}>{currentLabel}</Text>
            <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN width={12} height={12} color={PRIMITIVE_COLORS.resioBlue} />
          </>
        )}
      </Hoverable>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <Hoverable hover="none" pressedOpacity={1} style={styles.overlay} onPress={() => setIsOpen(false)}>
          <View style={[styles.dropdown, { backgroundColor: colors.card.bg, borderColor: colors.card.border }]}>
            <Text style={[styles.dropdownTitle, { color: colors.text.primary, borderBottomColor: colors.separator }]}>Sort by</Text>
            {CORK_BOARD_SORT_OPTIONS.map((option) => {
              const isSelected = option.value === sortBy;
              return (
                <AnchoredDropdownItem
                  key={option.value}
                  selected={isSelected}
                  bleed={-space.xs}
                  style={[styles.option, isSelected && !isWeb && { backgroundColor: colors.glass.buttonBg }]}
                  onPress={() => handleSelect(option.value)}
                  accessibilityRole="radio"
                  accessibilityLabel={option.label}
                  accessibilityState={{ selected: isSelected }}
                >
                  <HighlightPlate visible={isSelected} bleed={-space.xs} color={colors.glass.buttonBg} />
                  <Text style={[styles.optionText, { color: colors.text.primary }, isSelected && styles.optionTextSelected]}>
                    {option.label}
                  </Text>
                  {isSelected && (
                    <RESIDENTS_SVG_COMPONENTS.CHECK width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                  )}
                </AnchoredDropdownItem>
              );
            })}
          </View>
        </Hoverable>
      </Modal>
    </View>
  );
});

NewsfeedSortRow.displayName = 'NewsfeedSortRow';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  postCount: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  sortTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sortLabel: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlue,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  dropdown: {
    borderRadius: 14,
    width: '100%',
    maxWidth: 300,
    paddingVertical: 8,
    borderWidth: 1,
  },
  dropdownTitle: {
    ...REDESIGN_TYPOGRAPHY.postTitle,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionText: {
    fontSize: 15,
  },
  optionTextSelected: {
    color: PRIMITIVE_COLORS.resioBlue,
    fontWeight: '600',
  },
});
